import { JsonRpcSigner, Signer } from 'ethers';

import { ApiVault } from '#/components/vaults/shared/types';
import config from '#/config';
import { getCurrentStateChainConfig } from '#/config/configUtils';
import {
  KatanaExchange_v2__factory,
  FixedIncomeVaultProvider_v1__factory,
  FixedIncomeVaultProviderStateAggregator_v1__factory,
  ProfitShareVaultProvider_v1__factory,
  ProfitShareVaultProviderStateAggregator_v1__factory,
} from '#/lib/ethereum/contracts/contractsFromSdk';
import { getSigner } from '#/lib/ethereum/wallets';
import { triggerWalletChainSwitchIfPossible } from '#/lib/ethereum/wallets/switchEthereumChain';
import { getAnonymousRPCProvider } from '#/lib/ethereum/wallets/utils';
import { UnreachableCaseError } from '#/lib/utils';
import { IWallet } from '#/reduxState/wallet/walletReducer';

/**
 * Fixed Income Vault Provider (FIV) contract. `providerAddress` comes from the
 * vaults API response (`provider` field) or, for vault creation, from config.
 */
export function getFixedIncomeVaultProviderContract(
  providerAddress: string,
  signer?: Signer | JsonRpcSigner
) {
  return FixedIncomeVaultProvider_v1__factory.connect(
    providerAddress,
    // @ts-expect-error TS6 ethers dual-package types (lib.esm vs lib.commonjs)
    signer ?? getAnonymousRPCProvider()
  );
}

/**
 * Profit Share Vault Provider (Vv1) contract - share based ownership.
 * `providerAddress` comes from the vaults API response (`provider` field) or,
 * for vault creation, from config (`profitShareVaultProviderAddress`).
 */
export function getProfitShareVaultProviderContract(
  providerAddress: string,
  signer?: Signer | JsonRpcSigner
) {
  return ProfitShareVaultProvider_v1__factory.connect(
    providerAddress,
    // @ts-expect-error TS6 ethers dual-package types (lib.esm vs lib.commonjs)
    signer ?? getAnonymousRPCProvider()
  );
}

/**
 * Provider contract of a vault (`type` and `provider` of the vaults API
 * response). Both providers share the Managed Account interface (exit,
 * deposits toggle, configuration upgrades), so the union can be used directly
 * for those common calls.
 */
export function getVaultProviderContract(
  vault: Pick<ApiVault, 'type' | 'provider'>,
  signer?: Signer | JsonRpcSigner
) {
  switch (vault.type) {
    case 'fixedIncomeVaultV1':
      return getFixedIncomeVaultProviderContract(vault.provider, signer);
    case 'profitShareVaultV1':
      return getProfitShareVaultProviderContract(vault.provider, signer);
    default:
      throw new UnreachableCaseError(vault.type);
  }
}

/**
 * `stateAggregatorAddress` comes from the vaults API response
 * (`stateAggregator` field)
 */
export function getFixedIncomeVaultAggregatorContract(
  stateAggregatorAddress: string
) {
  return FixedIncomeVaultProviderStateAggregator_v1__factory.connect(
    stateAggregatorAddress,
    // @ts-expect-error TS6 ethers dual-package types (lib.esm vs lib.commonjs)
    getAnonymousRPCProvider()
  );
}

export function getProfitShareVaultAggregatorContract(
  stateAggregatorAddress: string
) {
  return ProfitShareVaultProviderStateAggregator_v1__factory.connect(
    stateAggregatorAddress,
    // @ts-expect-error TS6 ethers dual-package types (lib.esm vs lib.commonjs)
    getAnonymousRPCProvider()
  );
}

export function getExchangeContract() {
  return KatanaExchange_v2__factory.connect(
    config.chains.katanaChain.exchangeContract,
    // @ts-expect-error TS6 ethers dual-package types (lib.esm vs lib.commonjs)
    getAnonymousRPCProvider()
  );
}

export const getKatanaWalletSigner = async (wallet: IWallet) => {
  const chainConfig = getCurrentStateChainConfig('katanaChain');
  await triggerWalletChainSwitchIfPossible(
    wallet.walletType,
    chainConfig,
    await getSigner(wallet, undefined, false)
  );
  return await getSigner(wallet);
};
